import { FC, useContext, useEffect, useMemo } from 'react';
import DateUtils from 'utils/dateUtils';
import { InvoiceTabEnum } from '../constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectDueDate,
  selectInvoiceAutoFilledData,
  selectPaymentReminders,
  updateDueDate,
  updateInvoiceDate,
  updatePaymentReminders
} from 'containers/invoices/InvoicesSlice';
import { InvoiceContext } from 'containers/invoices/types';
import { useDueDateOptions } from 'containers/invoices/hooks/useDueDateOptions';
import { DueDateSection } from './DueDateSection';
import { recalculateReminderDates } from 'containers/invoices/utils';
import { AutoFilledIndicator } from 'components';

export interface InvoiceOptionsSectionProps {
  className?: string;
  context: React.Context<InvoiceContext>;
}

const InvoiceSection: FC<InvoiceOptionsSectionProps> = ({ context }) => {
  const { selectedInvoiceTab } = useContext<InvoiceContext>(context);
  const dispatch = useAppDispatch();
  const dueDate = useAppSelector(selectDueDate);
  const paymentReminders = useAppSelector(selectPaymentReminders);
  const autoFilledData = useAppSelector(selectInvoiceAutoFilledData);

  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.INVOICE_OPTIONS;

  const invoiceDate = useMemo(() => DateUtils.getStartOfDay(DateUtils.getNow()), []);

  useEffect(() => {
    dispatch(updateInvoiceDate(invoiceDate.toISOString()));
  }, [dispatch, invoiceDate]);

  const renderInvoiceDate = () => {
    const date = DateUtils.getDateInFormat({
      date: invoiceDate,
      dateFormat: 'dd MMM yyyy'
    });
    return date;
  };

  // Use shared hook for predefined due date options
  const predefinedDueDateOptions = useDueDateOptions(invoiceDate);

  const handleDueDateChange = (selectedDate: Date) => {
    const newDueDateISO = selectedDate ? selectedDate.toISOString() : null;
    dispatch(updateDueDate(newDueDateISO));

    // Recalculate reminder dates if reminders exist
    if (paymentReminders.length > 0 && newDueDateISO) {
      const updatedReminders = recalculateReminderDates(paymentReminders, newDueDateISO);
      dispatch(updatePaymentReminders(updatedReminders));
    }
  };

  const currentDueDate = dueDate ? DateUtils.parseISODateString(dueDate) : predefinedDueDateOptions[0]?.date;

  const isDueDateAutoFilled = useMemo(() => {
    if (autoFilledData?.invoice?.invoiceDueDate && currentDueDate) {
      return DateUtils.areSameDates(
        DateUtils.parseISODateString(autoFilledData.invoice.invoiceDueDate),
        currentDueDate
      );
    }
    return false;
  }, [autoFilledData, currentDueDate]);

  return (
    <div>
      <div className={`mb-2 text-xl font-bold text-heading ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
        Invoice options
        <AutoFilledIndicator isAutoFilled={isDueDateAutoFilled} />
      </div>
      <div
        className={`flex items-center justify-between gap-6 rounded-md border py-5 px-[30px]
        1439px:p-10 ${isTabSelected ? 'border-primaryBtn' : 'border-borderGray'}`}>
        {/* Invoice Date - Static display */}
        <div className="flex flex-row gap-5 text-17px">
          <div className="whitespace-nowrap font-semibold text-primaryText">Invoice date</div>
          <div className="whitespace-nowrap font-semibold text-accentText">{renderInvoiceDate()}</div>
        </div>

        {/* Due Date Section */}
        <DueDateSection
          currentDueDate={currentDueDate}
          invoiceDate={invoiceDate}
          onDueDateChange={handleDueDateChange}
        />
      </div>
    </div>
  );
};

export default InvoiceSection;
